import { useState, useEffect, useCallback } from 'react';
import translationService from '../services/translationService';

export interface UseTranslationReturn {
  t: (key: string, fallback?: string) => string;
  currentLanguage: string;
  currentLanguageName: string;
  supportedLanguages: Record<string, string>;
  changeLanguage: (languageCode: string) => Promise<void>;
  isRTL: boolean;
  isLoading: boolean;
  isLoaded: boolean;
}

export const useTranslation = (): UseTranslationReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [currentLanguageName, setCurrentLanguageName] = useState('English');

  // Inicializar el servicio de traducciones
  useEffect(() => {
    const initializeTranslations = async () => {
      try {
        setIsLoading(true);
        
        // El servicio ya se inicializa automáticamente en el constructor
        const lang = translationService.getCurrentLanguage();
        setCurrentLanguage(lang);
        setCurrentLanguageName(translationService.getCurrentLanguageName());
        
        // Cargar traducciones
        await translationService.loadTranslations(lang);
        
        setIsLoaded(true);
      } catch (error) {
        console.error('Error initializing translations:', error);
        // Fallback a inglés
        await translationService.loadTranslations('en');
        setCurrentLanguage('en');
        setCurrentLanguageName('English');
        setIsLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeTranslations();
  }, []);

  // Función para obtener traducciones
  const t = useCallback((key: string, fallback?: string): string => {
    return translationService.t(key, fallback);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // Función para cambiar idioma
  const changeLanguage = useCallback(async (languageCode: string): Promise<void> => {
    try {
      setIsLoading(true);
      await translationService.changeLanguage(languageCode);
      
      setCurrentLanguage(translationService.getCurrentLanguage());
      setCurrentLanguageName(translationService.getCurrentLanguageName());
      setIsLoaded(true);
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Verificar si es RTL
  const isRTL = translationService.isRTL();

  return {
    t,
    currentLanguage,
    currentLanguageName,
    supportedLanguages: translationService.getSupportedLanguages(),
    changeLanguage,
    isRTL,
    isLoading,
    isLoaded
  };
};
